import React, { useState } from "react";
import { motion } from "framer-motion";
import { HelmetProvider } from "react-helmet-async";
import emailjs from "emailjs-com";
import SEOHead from "../components/common/SEOHead";

const ApplyPremium = () => {
  const [formData, setFormData] = useState({
    from_name: "",
    email: "",
    role: "",
    experience: "",
    portfolio: "",
    motivation: "",
    availability: "",
    equipment: "",
  });

  const [isSending, setIsSending] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const seoData = {
    title: "Apply to Livneu - Join Sri Lanka's Premier VTuber Agency",
    description:
      "Apply to become a virtual star with Livneu. Join our talent roster as a Virtual Streamer, Live2D Artist, 3D Modeler, or other creative roles. Start your journey today.",
    image: "https://livneu.levelstair.com/ShareImage.png",
    url: "https://livneu.levelstair.com/apply",
    keywords:
      "VTuber application, join Livneu, virtual streamer audition, Live2D artist jobs, 3D modeling careers, virtual talent agency, LEVELSTAIR jobs",
  };

  const roleOptions = [
    "Virtual Streamer",
    "Live2D Artist",
    "3D Modeler",
    "Content Manager",
    "Community Moderator",
    "Technical Staff",
    "Voice Actor/Actress",
    "Video Editor",
    "Graphic Designer",
    "Social Media Manager",
    "Other (Please specify in motivation)",
  ];

  const experienceOptions = [
    "Complete Beginner",
    "Some Experience (< 1 year)",
    "Moderate Experience (1-3 years)",
    "Experienced (3-5 years)",
    "Expert (5+ years)",
    "Industry Professional",
  ];

  const availabilityOptions = [
    "Part-time (10-20 hours/week)",
    "Full-time (40+ hours/week)",
    "Flexible/Project-based",
    "Weekends only",
    "Evenings only",
    "Student (Limited availability)",
  ];

  // Handle Form Input Changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Send Email
  const sendEmail = (e) => {
    e.preventDefault();
    setIsSending(true);

    emailjs
      .sendForm(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_APPLY_ID,
        e.target,
        process.env.REACT_APP_EMAILJS_USER_ID
      )
      .then(
        (result) => {
          setShowSuccessMessage(true);
          setFormData({
            from_name: "",
            email: "",
            role: "",
            experience: "",
            portfolio: "",
            motivation: "",
            availability: "",
            equipment: "",
          });
          setIsSending(false);
        },
        (error) => {
          console.log(error.text);
          alert("Failed to send application. Please try again.");
          setIsSending(false);
        }
      );
  };

  const requirements = [
    {
      icon: "mic-outline",
      title: "Quality Audio",
      description: "Professional microphone and clear audio setup",
    },
    {
      icon: "desktop-outline",
      title: "Streaming Setup",
      description: "Reliable computer and stable internet connection",
    },
    {
      icon: "language-outline",
      title: "Communication",
      description: "Fluency in English and/or local languages",
    },
    {
      icon: "calendar-outline",
      title: "Commitment",
      description: "Consistent schedule and professional dedication",
    },
    {
      icon: "heart-outline",
      title: "Passion",
      description: "Genuine enthusiasm for virtual entertainment",
    },
    {
      icon: "people-outline",
      title: "Community",
      description: "Ability to engage and build audience relationships",
    },
  ];

  if (showSuccessMessage) {
    return (
      <HelmetProvider>
        <div className="min-h-screen flex items-center justify-center px-6">
          <SEOHead seoData={seoData} />

          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="glass-card p-12 text-center max-w-2xl"
          >
            <div className="w-24 h-24 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-8">
              <ion-icon
                name="checkmark-outline"
                style={{ fontSize: "3rem", color: "white" }}
              ></ion-icon>
            </div>

            <h1
              className="text-4xl font-bold text-black mb-6"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Application Submitted Successfully!
            </h1>

            <p
              className="text-xl text-gray-700 mb-8 leading-relaxed"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Thank you for your interest in joining Livneu! Our team
              will review your application and get back to you within 5-7
              business days.
            </p>

            <motion.a
              href="/"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-block glass-button px-8 py-4 text-lg no-underline"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Return Home
            </motion.a>
          </motion.div>
        </div>
      </HelmetProvider>
    );
  }

  return (
    <HelmetProvider>
      <div className="min-h-screen">
        <SEOHead seoData={seoData} />

        {/* Hero Section */}
        <section className="relative pt-32 pb-20 px-6 overflow-hidden bg-gradient-about">
          {/* Kawaii Background Effects */}
          <div className="absolute inset-0">
            <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-pastel-purple/15 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-pastel-blue/15 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute top-1/2 left-1/2 w-32 h-32 bg-pastel-mint/20 rounded-full blur-2xl animate-pulse"></div>
          </div>

          <div className="container mx-auto relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center max-w-4xl mx-auto"
            >
              <h1 className="text-5xl md:text-7xl font-title text-text-primary mb-6 leading-tight kawaii-glow">
                Join the{" "}
                <span className="text-text-accent">
                  Next Generation
                </span>
              </h1>

              <p className="text-xl md:text-2xl text-text-secondary mb-8 leading-relaxed font-body">
                Start your journey as a virtual star with Livneu. We're
                looking for passionate creators ready to revolutionize digital
                entertainment.
              </p>

              <p className="text-lg text-text-accent font-accent font-medium">
                Your digital stardom begins here.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Requirements Section */}
        <section className="py-20 px-6 bg-bg-primary">
          <div className="container mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl md:text-5xl font-title text-text-primary mb-6">
                What We're Looking For
              </h2>
              <p
                className="text-xl text-gray-700 max-w-3xl mx-auto"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Essential requirements to join our professional VTuber agency
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto mb-16">
              {requirements.map((req, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                  className="glass-card p-6 smooth-transition"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-400 to-blue-500 rounded-xl flex items-center justify-center mb-6">
                    <ion-icon
                      name={req.icon}
                      style={{ fontSize: "2rem", color: "white" }}
                    ></ion-icon>
                  </div>

                  <h3
                    className="text-xl font-bold text-black mb-4"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    {req.title}
                  </h3>

                  <p
                    className="text-gray-700 leading-relaxed"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    {req.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Application Form */}
        <section className="py-20 px-6">
          <div className="container mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2
                className="text-4xl md:text-5xl font-bold text-black mb-6"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Submit Your Application
              </h2>
              <p
                className="text-xl text-gray-700 max-w-3xl mx-auto"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Tell us about yourself and why you want to join Livneu
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-4xl mx-auto"
            >
              <form onSubmit={sendEmail} className="glass-card p-8 md:p-12">
                <div className="grid md:grid-cols-2 gap-8">
                  {/* Personal Information */}
                  <div className="space-y-6">
                    <div>
                      <label
                        className="block text-black font-semibold mb-3 text-lg"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                      >
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="from_name"
                        value={formData.from_name}
                        onChange={handleChange}
                        required
                        className="glass-input w-full px-6 py-4"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                        placeholder="Your full name"
                      />
                    </div>

                    <div>
                      <label
                        className="block text-black font-semibold mb-3 text-lg"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                      >
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="glass-input w-full px-6 py-4"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                        placeholder="your.email@example.com"
                      />
                    </div>

                    <div>
                      <label
                        className="block text-black font-semibold mb-3 text-lg"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                      >
                        Desired Role *
                      </label>
                      <select
                        name="role"
                        value={formData.role}
                        onChange={handleChange}
                        required
                        className="glass-input w-full px-6 py-4"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                      >
                        <option value="" className="text-gray-900">
                          Select a role
                        </option>
                        {roleOptions.map((role) => (
                          <option
                            key={role}
                            value={role}
                            className="text-gray-900"
                          >
                            {role}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label
                        className="block text-black font-semibold mb-3 text-lg"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                      >
                        Experience Level *
                      </label>
                      <select
                        name="experience"
                        value={formData.experience}
                        onChange={handleChange}
                        required
                        className="glass-input w-full px-6 py-4"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                      >
                        <option value="" className="text-gray-900">
                          Select experience level
                        </option>
                        {experienceOptions.map((exp) => (
                          <option
                            key={exp}
                            value={exp}
                            className="text-gray-900"
                          >
                            {exp}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Additional Information */}
                  <div className="space-y-6">
                    <div>
                      <label
                        className="block text-black font-semibold mb-3 text-lg"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                      >
                        Availability *
                      </label>
                      <select
                        name="availability"
                        value={formData.availability}
                        onChange={handleChange}
                        required
                        className="glass-input w-full px-6 py-4"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                      >
                        <option value="" className="text-gray-900">
                          Select availability
                        </option>
                        {availabilityOptions.map((avail) => (
                          <option
                            key={avail}
                            value={avail}
                            className="text-gray-900"
                          >
                            {avail}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label
                        className="block text-black font-semibold mb-3 text-lg"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                      >
                        Portfolio/Social Links
                      </label>
                      <textarea
                        name="portfolio"
                        value={formData.portfolio}
                        onChange={handleChange}
                        rows="4"
                        className="glass-input w-full px-6 py-4 resize-none"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                        placeholder="Share links to your work, social media, previous content, etc."
                      />
                    </div>

                    <div>
                      <label
                        className="block text-black font-semibold mb-3 text-lg"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                      >
                        Equipment & Setup
                      </label>
                      <textarea
                        name="equipment"
                        value={formData.equipment}
                        onChange={handleChange}
                        rows="4"
                        className="glass-input w-full px-6 py-4 resize-none"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                        placeholder="Describe your current equipment, computer specs, internet speed, etc."
                      />
                    </div>
                  </div>
                </div>

                {/* Full Width Fields */}
                <div className="mt-8">
                  <label
                    className="block text-black font-semibold mb-3 text-lg"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    Why do you want to join Livneu? *
                  </label>
                  <textarea
                    name="motivation"
                    value={formData.motivation}
                    onChange={handleChange}
                    required
                    rows="6"
                    className="glass-input w-full px-6 py-4 resize-none"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                    placeholder="Tell us about your passion for virtual entertainment, your goals, and what you hope to achieve with Livneu..."
                  />
                </div>

                {/* Submit Button */}
                <div className="mt-12 text-center">
                  <motion.button
                    type="submit"
                    disabled={isSending}
                    whileHover={{
                      scale: isSending ? 1 : 1.05,
                      boxShadow: "0 25px 50px rgba(151, 46, 240, 0.4)",
                    }}
                    whileTap={{ scale: 0.95 }}
                    className={`glass-button px-12 py-5 text-xl ${
                      isSending ? "opacity-75 cursor-not-allowed" : ""
                    }`}
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    {isSending
                      ? "Submitting Application..."
                      : "Submit Application"}
                  </motion.button>

                  <p
                    className="text-gray-700 mt-6"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    We'll review your application and get back to you within 5-7
                    business days.
                  </p>
                </div>
              </form>
            </motion.div>
          </div>
        </section>
      </div>
    </HelmetProvider>
  );
};

export default ApplyPremium;
